import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Mail, CheckCircle, XCircle, Send } from 'lucide-react';
import SEO from '../components/SEO';
import { authService } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUserAndTokens } = useAuth();
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    const emailFromState = (location.state as any)?.email;
    if (emailFromState) {
      setEmail(emailFromState);
    } else if (user?.email) {
      setEmail(user.email);
    }
  }, [user, location.state]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    if (!code.trim() || code.length !== 6) {
      setError('Please enter the 6-digit code from your email');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const { user: verifiedUser, tokens } = await authService.verifyEmail(email.trim().toLowerCase(), code.trim());
      setUserAndTokens(verifiedUser, tokens);
      setSuccess(true);
      setTimeout(() => navigate('/', { replace: true }), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Invalid or expired verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!email.trim()) {
      setError('Email address is required to resend the code');
      return;
    }

    setError('');
    setResendSuccess(false);
    setIsResending(true);

    try {
      await authService.resendVerificationEmail(email.trim().toLowerCase());
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to resend. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <>
      <SEO
        title="Verify Email - Book My Yoga TTC"
        description="Verify your email address to complete your registration."
        url="https://bookmyyogattc.com/verify-email"
      />
      <div className="pt-20 min-h-screen flex">
        {/* Left Side - Visual */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary via-primary-dark to-primary relative overflow-hidden">
          <div className="absolute inset-0">
            <img src="/images/cover.png" alt="Yoga" className="w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary-dark/80 to-primary/90"></div>
          </div>
          <div className="absolute inset-0 opacity-10">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="verifyPattern" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="white" strokeWidth="1" />
                  <circle cx="60" cy="60" r="35" fill="none" stroke="white" strokeWidth="0.5" />
                  <circle cx="60" cy="60" r="20" fill="none" stroke="white" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#verifyPattern)" />
            </svg>
          </div>
          <div className="relative z-10 flex flex-col items-center justify-center p-12 text-white w-full">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-md"
            >
              <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 3, repeat: Infinity }} className="text-5 mb-6">
                ✉️
              </motion.div>
              <h2 className="text-5 font-semibold mb-4">Check Your Email</h2>
              <p className="text-4 opacity-95 leading-relaxed">
                We&apos;ve sent a 6-digit verification code to your email. Enter it below to verify your account.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center bg-white p-6 lg:p-12">
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-md"
          >
            <div className="mb-6">
              <h1 className="text-5 font-semibold text-ivory-900 mb-2">Verify Your Email</h1>
              <p className="text-ivory-600">
                {email ? `Enter the 6-digit code sent to ${email}` : 'Enter the 6-digit code from your email'}
              </p>
            </div>

            {success ? (
              <div className="bg-white rounded-xl shadow-xl p-8 border border-ivory-100">
                <div className="text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                    className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4"
                  >
                    <CheckCircle className="h-10 w-10 text-green-600" />
                  </motion.div>
                  <h2 className="text-5 font-semibold text-ivory-900 mb-2">Email Verified!</h2>
                  <p className="text-ivory-600 mb-6">Redirecting...</p>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-xl p-8 border border-ivory-100">
                <form onSubmit={handleVerify} className="space-y-5">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-2 flex items-center space-x-2">
                      <XCircle className="h-5 w-5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  {resendSuccess && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-2 flex items-center space-x-2">
                      <CheckCircle className="h-5 w-5 flex-shrink-0" />
                      <span>Verification code sent! Check your inbox.</span>
                    </div>
                  )}

                  <div>
                    <label htmlFor="email" className="block text-ivory-700 font-medium mb-2">Email address</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white text-ivory-900"
                      placeholder="you@example.com"
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label htmlFor="code" className="block text-ivory-700 font-medium mb-2">6-digit verification code</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-ivory-400" />
                      <input
                        id="code"
                        name="code"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        required
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="w-full pl-12 pr-4 py-3 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white text-ivory-900 text-center text-xl tracking-[0.5em] font-mono placeholder-ivory-400"
                        placeholder="000000"
                        disabled={isLoading}
                      />
                    </div>
                    <p className="mt-2 text-2 text-ivory-500">Enter the code from the email we sent you.</p>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || !email.trim() || code.length !== 6}
                    className="w-full flex justify-center items-center py-3 px-6 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Verifying...
                      </>
                    ) : (
                      'Verify Email'
                    )}
                  </button>

                  <div className="text-center pt-4 border-t border-ivory-200 space-y-3">
                    <p className="text-2 text-ivory-600 mb-2">Didn&apos;t receive the code?</p>
                    <button
                      type="button"
                      onClick={handleResendEmail}
                      disabled={isResending || !email.trim()}
                      className="inline-flex items-center space-x-2 text-2 font-semibold text-primary hover:text-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isResending ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          <span>Resend Code</span>
                        </>
                      )}
                    </button>
                    <div>
                      <Link to="/login" className="text-2 text-ivory-600 hover:text-primary">
                        Back to Login
                      </Link>
                    </div>
                  </div>
                </form>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default VerifyEmail;
